import React from 'react';
import {
  DocumentTextIcon,
  PhotoIcon,
  Square2StackIcon,
  ViewfinderCircleIcon,
  HashtagIcon,
} from '@heroicons/react/24/outline';
import SaveButton from './SaveButton';
import SpreadControls from './SpreadControls';
import CoverPaperColorPicker from './CoverPaperColorPicker';

export default function SpreadToolbar({
  onAddElement,
  onSave,
  isDirty,
  isSaving,
  currentSpread,
  totalPages,
  onPreviousSpread,
  onNextSpread,
  showDieLines,
  onToggleDieLines,
  paperColor,
  onPaperColorChange,
  hasPageNumbers,
  onTogglePageNumbers,
}) {
  // Calculate if this is a cover spread (first or last spread)
  const maxSpreads = Math.ceil(totalPages / 2);
  const isCoverSpread = currentSpread === 0; // Only show for front cover

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <button
            onClick={() => onAddElement('text')}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50"
          >
            <DocumentTextIcon className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Add Text</span>
          </button>

          <button
            onClick={() => onAddElement('image')}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50"
          >
            <PhotoIcon className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Add Image</span>
          </button>

          <button
            onClick={() => onAddElement('shape')}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50"
          >
            <Square2StackIcon className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Add Shape</span>
          </button>

          <button
            onClick={onToggleDieLines}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm border transition-colors ${
              showDieLines
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ViewfinderCircleIcon
              className={`h-5 w-5 ${
                showDieLines ? 'text-indigo-600' : 'text-gray-600'
              }`}
            />
            <span className="text-sm font-medium">Die Lines</span>
          </button>

          <button
            onClick={onTogglePageNumbers}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm border transition-colors ${
              hasPageNumbers
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <HashtagIcon
              className={`h-5 w-5 ${
                hasPageNumbers ? 'text-blue-600' : 'text-gray-600'
              }`}
            />
            <span className="text-sm font-medium">Page Numbers</span>
          </button>
        </div>

        <div className="flex items-center gap-4">
          <SaveButton onClick={onSave} disabled={!isDirty || isSaving} />
          <SpreadControls
            currentSpread={currentSpread}
            totalPages={totalPages}
            onPreviousSpread={onPreviousSpread}
            onNextSpread={onNextSpread}
          />
        </div>
      </div>

      {/* Show paper color picker only for front cover */}
      {isCoverSpread && (
        <div className="flex items-center justify-start border-t pt-4">
          <CoverPaperColorPicker
            currentColor={paperColor}
            onChange={onPaperColorChange}
          />
          <span className="ml-4 text-sm text-gray-500">
            Front Cover Page
          </span>
        </div>
      )}
    </div>
  );
}
